import type { Components, Theme, CSSObject } from "@mui/material/styles";
import type { SemanticPalette } from "../../tokens/colorSemantic";
import { type StateSpec, stateToCss } from "../helpers";
import { RadiusToken } from "../../tokens/radii";

type Rmh = SemanticPalette;

type IconButtonColorSpec = {
  enabled: StateSpec;
  hovered: StateSpec;
  focused: StateSpec;
  pressed: StateSpec;
  disabled: StateSpec;
};

const MUI_SELECTORS = {
  hovered: "&:hover:not(.Mui-disabled)",
  focused: "&.Mui-focusVisible",
  pressed: "&:active:not(.Mui-disabled)",
  disabled: "&.Mui-disabled",
};

const colorStyle =
  (build: (rmh: Rmh) => IconButtonColorSpec) =>
  ({ theme }: { theme: Theme }): CSSObject => {
    const spec = build(theme.vars!.palette.rmh);
    return {
      ...stateToCss(spec.enabled),
      [MUI_SELECTORS.hovered]: stateToCss(spec.hovered),
      [MUI_SELECTORS.focused]: stateToCss(spec.focused),
      [MUI_SELECTORS.pressed]: stateToCss(spec.pressed),
      [MUI_SELECTORS.disabled]: stateToCss(spec.disabled),
    };
  };

const defaultSpec = (rmh: Rmh): IconButtonColorSpec => ({
  enabled: { color: rmh.icon.secondary },
  hovered: {
    color: rmh.icon.secondary,
    backgroundColor: rmh.paper["1-surface"].hover,
  },
  focused: {
    color: rmh.icon.secondary,
    backgroundColor: rmh.paper["1-surface"].rest,
    ring: rmh.focus.primary,
  },
  pressed: {
    color: rmh.icon.secondary,
    backgroundColor: rmh.paper["1-surface"].active,
  },
  disabled: { color: rmh.icon.disabled },
});

const primarySpec = (rmh: Rmh): IconButtonColorSpec => ({
  enabled: { color: rmh.icon.brand },
  hovered: {
    color: rmh.icon.brand,
    backgroundColor: rmh.paper["1-surface"].hover,
  },
  focused: {
    color: rmh.icon.brand,
    backgroundColor: rmh.paper["1-surface"].rest,
    ring: rmh.focus.primary,
  },
  pressed: {
    color: rmh.icon.brand,
    backgroundColor: rmh.paper["1-surface"].active,
  },
  disabled: { color: rmh.icon.disabled },
});

const errorSpec = (rmh: Rmh): IconButtonColorSpec => ({
  enabled: { color: rmh.icon.error },
  hovered: {
    color: rmh.icon.error,
    backgroundColor: rmh.paper["9-errorFaint"].hover,
  },
  focused: {
    color: rmh.icon.error,
    backgroundColor: rmh.paperElevation[10],
    ring: rmh.focus.error,
  },
  pressed: {
    color: rmh.icon.error,
    backgroundColor: rmh.paper["9-errorFaint"].active,
  },
  disabled: { color: rmh.icon.disabled },
});

// `inherit` color leaves text/icon color to cascade from the parent. The
// background overlay is a fixed rgba black at varying alphas, independent of
// color scheme — kept as raw values rather than tokenized.
const inheritSpec = (): IconButtonColorSpec => {
  const overlay = (alpha: number) => `rgba(0, 0, 0, ${alpha})`;
  return {
    enabled: { color: "inherit", backgroundColor: overlay(0.4) },
    hovered: { color: "inherit", backgroundColor: overlay(0.48) },
    focused: {
      color: "inherit",
      backgroundColor: overlay(0.4),
    },
    pressed: { color: "inherit", backgroundColor: overlay(0.52) },
    disabled: { color: "inherit", backgroundColor: overlay(0.12) },
  };
};

const inheritColorStyle = colorStyle((rmh) => {
  const base = inheritSpec();
  base.focused.ring = rmh.focus.primary;
  return base;
});

export const MuiIconButton: NonNullable<Components<Theme>["MuiIconButton"]> = {
  defaultProps: {
    disableRipple: true,
    color: "default",
    size: "medium",
  },
  styleOverrides: {
    root: ({ theme }) => ({
      borderRadius: RadiusToken.MAX,
      transition: theme.transitions.create(
        ["background-color", "color", "box-shadow"],
        { duration: theme.transitions.duration.shortest }
      ),
    }),
  },
  variants: [
    {
      props: { size: "small" },
      style: ({ theme }: { theme: Theme }) => ({
        padding: theme.spacing(1.25),
        "& .MuiSvgIcon-root": { fontSize: "1.5rem" },
      }),
    },
    {
      props: { size: "medium" },
      style: ({ theme }: { theme: Theme }) => ({
        padding: theme.spacing(2),
        "& .MuiSvgIcon-root": { fontSize: "1.5rem" },
      }),
    },
    {
      props: { size: "large" },
      style: ({ theme }: { theme: Theme }) => ({
        padding: theme.spacing(2),
        "& .MuiSvgIcon-root": { fontSize: "2rem" },
      }),
    },
    { props: { color: "default" }, style: colorStyle(defaultSpec) },
    { props: { color: "primary" }, style: colorStyle(primarySpec) },
    { props: { color: "error" }, style: colorStyle(errorSpec) },
    { props: { color: "inherit" }, style: inheritColorStyle },
  ],
};
